import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Gear, Robot, CreditCard, CloudArrowUp, Link, CheckCircle, XCircle,
  TiktokLogo, InstagramLogo, YoutubeLogo, FacebookLogo, XLogo, LinkedinLogo
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const integrationGroups = [
  {
    title: "AI Services",
    items: [
      { key: "openai_enabled", name: "ChatGPT / OpenAI", description: "Text generation, scripts, captions" },
      { key: "elevenlabs_enabled", name: "ElevenLabs", description: "Voice cloning and narration" },
      { key: "synthesia_enabled", name: "Synthesia", description: "AI video generation" },
      { key: "heygen_enabled", name: "HeyGen", description: "Avatar video creation" },
      { key: "pictory_enabled", name: "Pictory", description: "Video editing automation" },
    ]
  },
  {
    title: "Payment & Academy",
    items: [
      { key: "stripe_enabled", name: "Stripe", description: "Payment processing" },
      { key: "whop_enabled", name: "Whop", description: "Academy platform integration" },
    ]
  },
  {
    title: "Social Media",
    items: [
      { key: "tiktok_enabled", name: "TikTok", description: "Auto-publish short videos", icon: TiktokLogo },
      { key: "instagram_enabled", name: "Instagram", description: "Reels and posts", icon: InstagramLogo },
      { key: "youtube_enabled", name: "YouTube", description: "Shorts and videos", icon: YoutubeLogo },
      { key: "facebook_enabled", name: "Facebook", description: "Page posts and reels", icon: FacebookLogo },
      { key: "twitter_enabled", name: "X (Twitter)", description: "Tweets and threads", icon: XLogo },
      { key: "linkedin_enabled", name: "LinkedIn", description: "Professional content", icon: LinkedinLogo },
    ]
  },
];

const IntegrationToggle = ({ integration, enabled, onToggle }) => {
  const Icon = integration.icon;
  
  return (
    <div className="flex items-center justify-between p-4 bg-zinc-900/50 border border-white/5 rounded-xl hover:border-white/10 transition-colors">
      <div className="flex items-center gap-3">
        {Icon ? (
          <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
            <Icon size={22} className="text-white" />
          </div>
        ) : (
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/20 flex items-center justify-center">
            <Robot size={22} className="text-amber-500" />
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-white">{integration.name}</p>
          <p className="text-xs text-zinc-500">{integration.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {enabled && <CheckCircle size={18} className="text-emerald-400" weight="fill" />}
        <Switch
          checked={enabled}
          onCheckedChange={(checked) => onToggle(integration.key, checked)}
          data-testid={`toggle-${integration.key}`}
        />
      </div>
    </div>
  );
};

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (err) {
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, value) => {
    setSaving(true);
    try {
      const newSettings = { ...settings, [key]: value };
      await axios.put(`${API}/settings`, { [key]: value });
      setSettings(newSettings);
      toast.success(`${value ? 'Enabled' : 'Disabled'} successfully`);
    } catch (err) {
      toast.error("Failed to update setting");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Settings
          </h1>
          <p className="text-zinc-400">Manage your integrations and preferences</p>
        </div>
      </div>

      {/* Integration Groups */}
      <div className="space-y-8">
        {integrationGroups.map((group) => (
          <motion.div
            key={group.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {group.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {group.items.map((integration) => (
                <IntegrationToggle
                  key={integration.key}
                  integration={integration}
                  enabled={settings[integration.key] || false}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* API Keys Section */}
      <div className="mt-12">
        <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          API Configuration
        </h2>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
          <p className="text-sm text-zinc-400 mb-4">
            API keys are securely stored in environment variables. Contact your administrator to update credentials.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-zinc-400">OpenAI / Emergent Key</Label>
              <Input
                type="password"
                value="sk-emergent-***********"
                disabled
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
              <p className="text-xs text-emerald-400 flex items-center gap-1">
                <CheckCircle size={14} />
                Connected via Emergent LLM Key
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Stripe (Test Mode)</Label>
              <Input
                type="password"
                value="sk_test_***********"
                disabled
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
              <p className="text-xs text-emerald-400 flex items-center gap-1">
                <CheckCircle size={14} />
                Test key configured
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <p className="text-sm text-amber-400">
              <strong>Note:</strong> Social media and video generation integrations require API credentials from each platform. These are currently in mock mode for demonstration.
            </p>
          </div>
        </div>
      </div>

      {/* Branding Section */}
      <div className="mt-12">
        <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Branding
        </h2>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-zinc-400">Academy Name</Label>
              <Input
                value="IPVALUE Digital Business Academy"
                disabled
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Presenter Name</Label>
              <Input
                value="Charles Loua"
                disabled
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing / Add Funds Section */}
      <div className="mt-12">
        <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Billing & Funds
        </h2>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-zinc-950 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Current Balance</p>
              <p className="text-3xl font-bold text-emerald-400">$0.00</p>
            </div>
            <div className="bg-zinc-950 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">This Month Spent</p>
              <p className="text-3xl font-bold text-white">$0.00</p>
            </div>
            <div className="bg-zinc-950 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Posts Remaining</p>
              <p className="text-3xl font-bold text-cyan-400">0</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <Label className="text-zinc-400">Add Funds</Label>
            <div className="flex flex-wrap gap-3">
              {[10, 25, 50, 100, 250].map((amount) => (
                <Button
                  key={amount}
                  onClick={() => toast.info(`Stripe checkout for $${amount} - Connect Stripe live key to enable`)}
                  className="h-12 px-6 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-semibold"
                >
                  ${amount}
                </Button>
              ))}
            </div>
            <p className="text-xs text-zinc-500">
              Funds are used for social media posting (API costs), video generation, and voice synthesis.
            </p>
          </div>
          
          <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
            <p className="text-sm text-cyan-400">
              <strong>Tip:</strong> Enable auto-reload in your Stripe settings to never run out of posting credits.
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <Button
          disabled={saving}
          className="h-12 px-8 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
